/**
 * Parses metadata from markdown content
 * Extracts title, description, category, location from # Metadata section
 * Also returns content without the metadata section
 */
export const parseMetadata = (markdownContent) => {
  const lines = markdownContent.split('\n');
  
  // Find metadata section
  const metadataStartIndex = lines.findIndex(line => line.trim() === '# Metadata');
  
  if (metadataStartIndex === -1) {
    // No metadata section found, return original content
    return {
      metadata: {},
      contentWithoutMetadata: markdownContent
    };
  }
  
  // Find where metadata section ends (next # heading or end of file)
  let metadataEndIndex = lines.length;
  for (let i = metadataStartIndex + 1; i < lines.length; i++) {
    if (lines[i].trim().startsWith('# ') && lines[i].trim() !== '# Metadata') {
      metadataEndIndex = i;
      break;
    }
  }
  
  // Extract metadata lines
  const metadataLines = lines.slice(metadataStartIndex + 1, metadataEndIndex);
  
  // Parse metadata key-value pairs
  const metadata = {};
  metadataLines.forEach(line => {
    const trimmedLine = line.trim();
    if (trimmedLine && trimmedLine.includes(':')) {
      const colonIndex = trimmedLine.indexOf(':');
      const key = trimmedLine.substring(0, colonIndex).trim();
      const value = trimmedLine.substring(colonIndex + 1).trim();
      
      if (key && value) {
        metadata[key.toLowerCase()] = value;
      }
    }
  });
  
  // Create content without metadata section
  const contentLines = [
    ...lines.slice(0, metadataStartIndex),
    ...lines.slice(metadataEndIndex)
  ];
  
  const contentWithoutMetadata = contentLines.join('\n').trim();
  
  return {
    metadata,
    contentWithoutMetadata
  };
};

/**
 * Converts location string to coordinates
 * For now, uses a simple mapping - could be expanded to use a geocoding service
 */
export const locationToCoordinates = (locationString) => {
  // Simple location mapping for known addresses
  const locationMap = {
    '1201 St Laurent Blvd, Montreal, Quebec H2X 2S6': {
      lat: 45.5156,
      lng: -73.5724
    },
    '1201 Saint Laurent Boulevard, Montreal': {
      lat: 45.5156,
      lng: -73.5724
    },
    '3956 St Laurent Blvd Étage 3, Montreal, Quebec H2W 1Y3': {
      lat: 45.5289,
      lng: -73.5967
    }
  };
  
  // Check for exact match first
  if (locationMap[locationString]) {
    return locationMap[locationString];
  }
  
  // Check for partial matches (e.g., if address format varies)
  for (const [key, coords] of Object.entries(locationMap)) {
    if (locationString.includes('1201') && locationString.includes('St Laurent')) {
      return coords;
    }
  }
  
  // Default Montreal coordinates if no match found
  return {
    lat: 45.5017,
    lng: -73.5673
  };
};

/**
 * Generates a unique ID from title
 */
export const generateId = (title) => {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .trim();
};